import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useStore';
import { collaborative as collaborativeApi } from '../lib/api';
import { ArrowLeft, Plus, Edit3, Send, Check } from 'lucide-react';
import MarkdownEditor from '../components/MarkdownEditor';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

export default function CollaborativePostDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token)!;
  const user = useAuthStore((state) => state.user)!;
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [addingSection, setAddingSection] = useState(false);
  const [editingSection, setEditingSection] = useState<any>(null);
  const [sectionContent, setSectionContent] = useState('');
  const [showPublishModal, setShowPublishModal] = useState(false);

  useEffect(() => {
    loadPost();
  }, [id]);

  async function loadPost() {
    if (!id) return;
    setLoading(true);
    try {
      const data = await collaborativeApi.getPost(token, id);
      setPost(data);
    } catch (error) {
      toast.error('Failed to load post');
    } finally {
      setLoading(false);
    }
  }

  async function handleJoin() {
    try {
      await collaborativeApi.joinPost(token, id!);
      toast.success('Joined! Add your section below.');
      setAddingSection(true);
      setSectionContent('');
      loadPost();
    } catch (error: any) {
      toast.error(error.error || 'Failed to join');
    }
  }

  async function handleSaveSection() {
    if (!sectionContent.trim()) {
      toast.error('Section content cannot be empty');
      return;
    }

    try {
      if (editingSection) {
        await collaborativeApi.updateSection(token, id!, editingSection.id, sectionContent);
        toast.success('Section updated');
        setEditingSection(null);
      } else {
        await collaborativeApi.addSection(token, id!, sectionContent);
        toast.success('Section added');
        setAddingSection(false);
      }
      setSectionContent('');
      loadPost();
    } catch (error) {
      toast.error('Failed to save section');
    }
  }

  function startEditingSection(section: any) {
    setEditingSection(section);
    setSectionContent(section.content);
    setAddingSection(false);
  }

  function startAddingSection() {
    setAddingSection(true);
    setSectionContent('');
    setEditingSection(null);
  }

  function cancelEditing() {
    setAddingSection(false);
    setEditingSection(null);
    setSectionContent('');
  }

  async function handlePublish() {
    try {
      await collaborativeApi.publishPost(token, id!, true);
      toast.success('Post published!');
      setShowPublishModal(false);
      loadPost();
    } catch (error: any) {
      toast.error(error.error || 'Failed to publish');
    }
  }

  if (loading) {
    return <div className="pl-4 lg:pl-6 py-10"><span className="kicker">Loading…</span></div>;
  }

  if (!post) {
    return <div className="pl-4 lg:pl-6 py-10"><span className="kicker text-signal">Post not found</span></div>;
  }

  const isAuthor = post.authors.some((a: any) => a.userId === user.id);
  const isInitiator = post.authors.find((a: any) => a.role === 'initiator')?.userId === user.id;
  const isDraft = post.status === 'draft';

  return (
    <div className="min-h-full bg-canvas overflow-y-auto page-transition">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 flex items-stretch border-b border-edge bg-canvas">
        <button onClick={() => navigate('/collaborate')} className="tool">
          <ArrowLeft className="w-3.5 h-3.5" /> Collaborate
        </button>
      </div>

      <div className="pl-4 pr-4 lg:pl-6 lg:pr-8 py-8">
        {/* Post Header */}
        <div className="mb-6 max-w-prose">
          <div className="kicker mb-1">
            <span className={post.status === 'published' ? 'text-pine' : 'text-signal'}>{post.status}</span>
            {' · '}{post.authors.length} {post.authors.length === 1 ? 'contributor' : 'contributors'} · {post.sections.length} {post.sections.length === 1 ? 'section' : 'sections'}
          </div>
          <h1 className="text-3xl mb-2">{post.title}</h1>
          {post.summary && <p className="text-ink-secondary mb-4">{post.summary}</p>}

          {/* Trigger Article */}
          <div className="border-t border-b border-edge py-3 mb-4">
            <div className="kicker mb-1">Sparked by</div>
            <div className="font-semibold text-ink">{post.triggerArticle.title}</div>
            <button onClick={() => navigate(`/article/${post.triggerArticle.id}`)} className="kicker hover:text-ink mt-1">
              View original article →
            </button>
          </div>

          {/* Contributors */}
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <span className="kicker">Contributors</span>
            {post.authors.map((author: any) => (
              <div key={author.id} className="flex items-center gap-2">
                {author.user.avatar ? (
                  <img src={author.user.avatar} alt="" className="w-5 h-5 rounded-full avatar-duotone" />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-ink text-paper flex items-center justify-center text-xs">
                    {author.user.username[0].toUpperCase()}
                  </div>
                )}
                <button
                  onClick={() => navigate(`/user/${author.user.id}`)}
                  className="text-sm font-semibold text-ink hover:text-accent-600"
                >
                  {author.user.displayName || author.user.username}
                </button>
                {author.role === 'initiator' && <span className="kicker">initiator</span>}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-2 flex-wrap">
            {!isAuthor && (
              <button onClick={handleJoin} className="btn btn-accent">
                <Plus className="w-3.5 h-3.5" /> Join & contribute
              </button>
            )}
            {isAuthor && !addingSection && !editingSection && (
              <button onClick={startAddingSection} className="btn btn-accent">
                <Plus className="w-3.5 h-3.5" /> Add section
              </button>
            )}
            {isDraft && isInitiator && post.sections.length > 0 && (
              <button onClick={() => setShowPublishModal(true)} className="btn btn-ink">
                <Check className="w-3.5 h-3.5" /> Publish
              </button>
            )}
          </div>
        </div>

        {/* Add/Edit Section Form */}
        {(addingSection || editingSection) && (
          <div className="mb-8 border-t border-b border-edge py-4">
            <div className="kicker mb-3">{editingSection ? 'Edit section' : 'New section'}</div>
            <MarkdownEditor value={sectionContent} onChange={setSectionContent} minHeight="300px" />
            <div className="flex gap-2 mt-4">
              <button onClick={handleSaveSection} className="btn btn-accent">
                <Send className="w-3.5 h-3.5" /> {editingSection ? 'Update section' : 'Add section'}
              </button>
              <button onClick={cancelEditing} className="btn btn-quiet">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Sections */}
        <div className="space-y-6">
          {post.sections.length === 0 ? (
            <div className="py-10 max-w-list"><div className="kicker mb-1">Empty</div><p className="text-ink-secondary">No sections yet. Be the first to contribute.</p></div>
          ) : (
            post.sections.map((section: any, index: number) => (
              <div key={section.id} className="border-b border-edge pb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {section.author.avatar ? (
                      <img src={section.author.avatar} alt="" className="w-8 h-8 rounded-full object-cover avatar-duotone" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-ink text-paper flex items-center justify-center">
                        {section.author.username[0].toUpperCase()}
                      </div>
                    )}
                    <div>
                      <button
                        onClick={() => navigate(`/user/${section.author.id}`)}
                        className="font-semibold text-ink hover:text-accent-600"
                      >
                        {section.author.displayName || section.author.username}
                      </button>
                      <div className="stamp">№ {String(index + 1).padStart(3, '0')}</div>
                    </div>
                  </div>
                  {section.authorId === user.id && !addingSection && !editingSection && (
                    <button
                      onClick={() => startEditingSection(section)}
                      className="btn btn-quiet min-h-0 py-1 px-2"
                    >
                      <Edit3 className="w-3.5 h-3.5" /> Edit
                    </button>
                  )}
                </div>
                <div className="article-content max-w-prose">
                  <ReactMarkdown
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      p: ({ children }) => <p className="mb-4">{children}</p>,
                      h1: ({ children }) => <h1 className="text-3xl font-bold mb-4 mt-6">{children}</h1>,
                      h2: ({ children }) => <h2 className="text-2xl font-bold mb-3 mt-5">{children}</h2>,
                      h3: ({ children }) => <h3 className="text-xl font-bold mb-2 mt-4">{children}</h3>,
                      ul: ({ children }) => <ul className="list-disc ml-6 mb-4">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal ml-6 mb-4">{children}</ol>,
                      li: ({ children }) => <li className="mb-1">{children}</li>,
                      a: ({ children, href }) => <a href={href} className="text-accent-600 hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
                      strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                      em: ({ children }) => <em className="italic">{children}</em>,
                      code: ({ children }) => <code className="bg-surface-sunken px-1 py-0.5 text-sm font-mono">{children}</code>,
                      pre: ({ children }) => <pre className="bg-surface-sunken border border-edge-subtle p-4 mb-4 overflow-x-auto">{children}</pre>,
                    }}
                  >
                    {section.content}
                  </ReactMarkdown>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Publish Modal */}
      {showPublishModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-animate">
          <div className="bg-canvas border border-ink p-6 w-full max-w-md mx-4 modal-animate">
            <div className="kicker mb-1">Publish</div>
            <h3 className="text-xl mb-3">Publish this post?</h3>
            <p className="text-ink-secondary mb-6">
              It goes to the instance feed. You can still edit your sections afterward.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowPublishModal(false)} className="btn btn-quiet">Cancel</button>
              <button onClick={handlePublish} className="btn btn-accent">Publish</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
